import { useNavState } from '@/common/navState/NavStateContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { far, fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Card, CardContent, CardHeader, Collapse, IconButton, TextField, Typography } from '@mui/material';
import Fuse from 'fuse.js';
import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Title } from '../../common/title/Title';

export const Help = () => {
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(null); // Track which card is expanded
  const [helpData, setHelpData] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const { navState } = useNavState();
  const entity = navState?.entity;

  const rsrc = useRsrc();
  useEffect(() => {
    rsrc.data('admin', 'HELP_CENTER_DATA_PLUGIN').then((data) => {
      setHelpData(Array.isArray(data) ? data : []);
    });
  }, []);

  /*
   * Filter results based on search bar
   */
  useEffect(() => {
    const fuse = new Fuse(helpData, {
      keys: ['question', 'answer'], // Fields to search in
      threshold: 0.3, // Controls how fuzzy the search is (lower = more strict)
    });

    if (!query && entity) {
      setFilteredResults(fuse.search(entity).map((result) => result.item));
      return;
    }

    setFilteredResults(fuse.search(query).map((result) => result.item));
  }, [query, helpData, entity]);

  return (
    <>
      {/* Title */}
      <Title icon={fas.faCircleQuestion} title="Help Center"></Title>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          padding: '20px',
        }}
      >
        {/* Search Bar */}
        <TextField
          fullWidth
          placeholder="Search help topics..."
          variant="outlined"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{
            maxWidth: '500px',
            borderRadius: '50px',
            '& .MuiOutlinedInput-root': {
              borderRadius: '50px',
              boxShadow: '2px 2px 8px rgba(0, 0, 0, 0.1)',
              '&:hover fieldset': { borderColor: '#007bff' },
              '&.Mui-focused fieldset': { borderColor: '#007bff', boxShadow: '0 0 10px rgba(0, 123, 255, 0.3)' },
            },
          }}
        />

        {!query && entity && (
          <Typography sx={{ textAlign: 'center', color: '#777', mt: 2 }}>Suggested questions...</Typography>
        )}

        {/* Search Results */}
        <Box
          sx={{
            width: { xs: '90%', sm: '60%', md: '50%' },
            mt: 2,
            '& .MuiCard-root': {
              '&:not(:last-child)': { borderBottom: 'none' },
              '&:first-child': { borderTopLeftRadius: '10px', borderTopRightRadius: '10px' },
              '&:last-child': { borderBottomLeftRadius: '10px', borderBottomRightRadius: '10px' },
            },
          }}
        >
          {filteredResults.length > 0 ?
            filteredResults.map((item) => (
              <Card
                disableRipple
                elevation={0}
                raised={false}
                variant="outlined"
                key={item.id}
                sx={(theme) => ({
                  borderRadius: '0px',
                  transition: '0.3s',
                  '&:hover': { boxShadow: theme.shadows[2] },
                  cursor: 'pointer',
                })}
                onClick={() => setExpanded(expanded === item.question ? null : item.question)} // Toggle expand
              >
                <CardHeader
                  title={<Typography>{item.question}</Typography>}
                  action={
                    <IconButton disableRipple>
                      <FontAwesomeIcon
                        icon={far.faChevronUp}
                        style={{
                          transition: 'transform 0.3s',
                          transform: expanded === item.question ? 'rotate(180deg)' : 'rotate(0deg)',
                          cursor: 'pointer',
                        }}
                      />
                    </IconButton>
                  }
                ></CardHeader>

                <Collapse in={expanded === item.question} timeout="auto" unmountOnExit>
                  <CardContent>
                    <Box sx={{ mt: 2 }}>
                      <ReactMarkdown components={{ p: Typography }}>{item.answer}</ReactMarkdown>
                    </Box>
                  </CardContent>
                </Collapse>
              </Card>
            ))
          : query && <Typography sx={{ textAlign: 'center', color: '#777', mt: 2 }}>No results found</Typography>}
        </Box>
      </Box>
    </>
  );
};
